import React from 'react';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import {
  Checkbox,
  Combobox,
  DownloadIcon,
  IconButton,
  Pane,
  Position,
  SearchIcon,
  Select,
  Text,
  TextInput,
  Tooltip,
} from 'evergreen-ui';
import { stationsFilter4PropsSelector } from 'Features/App/appSlice';
import {
  cgmsByFgbuId,
  fgbuRegions,
} from 'Services/api/regionsApi';
import { filterRegionsSelector } from 'Services/auth.service';
import FormField from 'Utils/FormField';
import { useLazyGetDevicesCVSQuery } from 'Services/api/devicesApi';
import Details from 'Shared/Details';

const useStyles = createUseStyles({
  searchForm: {
    display: 'grid',
    gap: '.5em',
    gridAutoFlow: 'column',
    alignItems: 'center',
  },
  summary: {
    cursor: 'pointer',
  },
  controlBar: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    columnGap: '.5em',
    // justifyContent: 'center',
    alignItems: 'center',
  },
  controlBarRow: {
    display: 'grid',
    gridAutoFlow: 'row',
    gridAutoColumns: 'max-content',
    gap: '.5em',
  },
});

export const SearchForm = React.memo(({
  onChangeParams,
  params,
  sizes,
  changed,
  onChangeChecked,
  isFetchMessages,
  searchMessages,
  onChange,
  onChangeDate,
}) => {
  const filterSelector = useSelector(stationsFilter4PropsSelector);

  const [createDevicesCVS, { isFetching: isCreateingCVS }] = useLazyGetDevicesCVSQuery();

  const filterRegions = useSelector(filterRegionsSelector);
  const fgbu = useSelector(fgbuRegions);
  const cgms = useSelector(cgmsByFgbuId(filterSelector.fgbu?.id));
  const classes = useStyles();
  const isInvalid = React.useMemo(() => !(filterSelector?.fgbu?.id || filterSelector?.cgms?.id), [filterSelector]);

  const fetchDataForCsv = React.useCallback(() => {
    const fetchParams = {
      ...params,
      sort: 'desc',
      programTypeId: 1,
      ...((filterSelector?.cgms || filterSelector?.fgbu) && { regionId: filterSelector?.cgms?.id || filterSelector?.fgbu?.id }),
    };
    createDevicesCVS(fetchParams);
  }, [createDevicesCVS, filterSelector, params]);
  return (
    <Details label="Параметры поиска">
      <Pane className={classes.controlBar}>
        <Pane className={classes.controlBarRow}>
          <Pane className={classes.controlBar}>

            <Text>Показывать по</Text>
            <Select
              name="size"
              value={params.size}
              width={80}
              onChange={onChangeParams}
            >
              {sizes.map((size) => <option key={size}>{size}</option>)}
            </Select>

            <Checkbox label="Вкл/Выкл" onChange={onChangeChecked} checked={params.enabled} />

            <IconButton
              disabled={isFetchMessages || !(filterSelector.fgbu || filterSelector.cgms)}
              icon={SearchIcon}
              intent="success"
              onClick={searchMessages}
              appearance={changed ? 'primary' : 'default'}
            />
            <Tooltip content="Экспорт в CSV" position={Position.TOP}>
              <IconButton
                disabled={isFetchMessages || !(filterSelector.fgbu || filterSelector.cgms)}
                icon={DownloadIcon}
                appearance="primary"
                intent="success"
                isLoading={isCreateingCVS}
                onClick={() => fetchDataForCsv()}
              />
            </Tooltip>
          </Pane>
          <FormField label="Регион">
            <Pane display="grid" gap=".5em" gridAutoFlow="column" gridAutoColumns="max-content" alignItems="center">
              <Combobox
                placeholder="ФГБУ*"
                selectedItem={filterSelector.fgbu || ''}
                value={filterSelector.fgbu?.id || ''}
                width={300}
                autocompleteProps={{
                  popoverMinWidth: 450,
                }}
                onChange={onChange}
                itemToString={(item) => item && (Object.keys(item).length ? `${item.name}` : '')}
                items={fgbu.filter(({ id }) => filterRegions.find((r) => +r === id))}
                inputProps={{
                  isInvalid,
                }}
              />
              <Combobox
                placeholder="ЦГМС"
                selectedItem={filterSelector.cgms || ''}
                value={filterSelector.cgms?.id || ''}
                width={300}
                autocompleteProps={{
                  popoverMinWidth: 450,
                }}
                onChange={onChange}
                disabled={!cgms.length}
                itemToString={(item) => item && (Object.keys(item).length ? `${item.name}` : '' || '')}
                items={cgms.filter(({ id }) => filterRegions.find((r) => +r === id))}
              />
            </Pane>
          </FormField>

          <FormField label="QC">
            <Pane className={classes.searchForm}>
              <Text>от</Text>
              <TextInput
                name="qcStart"
                deviceatt="qc"
                headername="QC"
                onChange={onChangeParams}
                defaultValue={params.qcStart || ''}
              />
              <Text>до</Text>
              <TextInput
                name="qcEnd"
                deviceatt="qc"
                headername="QC"
                onChange={onChangeParams}
                defaultValue={params.qcEnd || ''}
              />
            </Pane>
          </FormField>
          <Pane className={classes.searchForm}>
            <FormField label="Заводской номер">
              <TextInput
                headername="Заводской номер"
                deviceatt="serialNumber"
                name="serialNumber"
                onChange={onChangeParams}
                defaultValue={params.serialNumber || ''}
              />
            </FormField>
            <FormField label="Инвентаризационный номер">
              <TextInput
                name="inventoryNum"
                deviceatt="inventoryNum"
                headername="Инвентаризационный номер"
                onChange={onChangeParams}
                defaultValue={params.inventoryNum || ''}
              />
            </FormField>
          </Pane>
          <FormField label="Дата ввода в эксплуатацию">
            <Pane className={classes.searchForm}>
              <Text>c</Text>
              <TextInput
                headername="Дата ввода в эксплуатацию"
                name="commissioningDateStart"
                deviceatt="dateCommissioningUTC"
                defaultValue={params.commissioningDateStart || ''}
                max={params.commissioningDateEnd}
                onChange={(e) => onChangeDate(e, 'start', 'commissioningDateStart', 'commissioningDateEnd')}
                type="datetime-local"
              />
              <Text>по</Text>
              <TextInput
                headername="Дата ввода в эксплуатацию"
                name="commissioningDateEnd"
                deviceatt="dateCommissioningUTC"
                defaultValue={params.commissioningDateEnd || ''}
                min={params.commissioningDateStart}
                onChange={(e) => onChangeDate(e, 'end', 'commissioningDateStart', 'commissioningDateEnd')}
                type="datetime-local"
              />
            </Pane>
          </FormField>

          <FormField label="Дата архивации">
            <Pane className={classes.searchForm}>
              <Text>c</Text>
              <TextInput
                headername="Дата архивации"
                name="archivingDateStart"
                deviceatt="dateArchivingUTC"
                defaultValue={params.archivingDateStart || ''}
                max={params.archivingDateEnd}
                onChange={(e) => onChangeDate(e, 'start', 'archivingDateStart', 'archivingDateEnd')}
                type="datetime-local"
              />
              <Text>по</Text>
              <TextInput
                headername="Дата архивации"
                name="archivingDateEnd"
                deviceatt="dateArchivingUTC"
                defaultValue={params.archivingDateEnd || ''}
                min={params.archivingDateStart}
                onChange={(e) => onChangeDate(e, 'end', 'archivingDateStart', 'archivingDateEnd')}
                type="datetime-local"
              />
            </Pane>
          </FormField>

          <FormField label="Дата поверки">
            <Pane className={classes.searchForm}>
              <Text>c</Text>
              <TextInput
                headername="Дата поверки"
                deviceatt="dateVerificationUTC"
                name="verificationDateStart"
                defaultValue={params.verificationDateStart || ''}
                max={params.verificationDateEnd}
                onChange={(e) => onChangeDate(e, 'start', 'verificationDateStart', 'verificationDateEnd')}
                type="datetime-local"
              />
              <Text>по</Text>
              <TextInput
                headername="Дата поверки"
                deviceatt="dateVerificationUTC"
                name="verificationDateEnd"
                defaultValue={params.verificationDateEnd || ''}
                min={params.verificationDateStart}
                onChange={(e) => onChangeDate(e, 'end', 'verificationDateStart', 'verificationDateEnd')}
                type="datetime-local"
              />
            </Pane>
          </FormField>

          <FormField label="Дата следующей поверки">
            <Pane className={classes.searchForm}>
              <Text>c</Text>
              <TextInput
                headername="Дата следующей поверки"
                deviceatt="dateNextVerificationUTC"
                name="nextVerificationDateStart"
                defaultValue={params.nextVerificationDateStart || ''}
                max={params.nextVerificationDateEnd}
                onChange={(e) => onChangeDate(e, 'start', 'nextVerificationDateStart', 'nextVerificationDateEnd')}
                type="datetime-local"
              />
              <Text>по</Text>
              <TextInput
                headername="Дата следующей поверки"
                deviceatt="dateNextVerificationUTC"
                name="nextVerificationDateEnd"
                defaultValue={params.nextVerificationDateEnd || ''}
                min={params.nextVerificationDateStart}
                onChange={(e) => onChangeDate(e, 'end', 'nextVerificationDateStart', 'nextVerificationDateEnd')}
                type="datetime-local"
              />
            </Pane>
          </FormField>

          <FormField label="Дата фактического списания">
            <Pane className={classes.searchForm}>
              <Text>c</Text>
              <TextInput
                headername="Дата фактического списания"
                deviceatt="dateWriteoffActualUTC"
                name="writeoffActualDateStart"
                defaultValue={params.writeoffActualDateStart || ''}
                max={params.writeoffActualDateEnd}
                onChange={(e) => onChangeDate(e, 'start', 'writeoffActualDateStart', 'writeoffActualDateEnd')}
                type="datetime-local"
              />
              <Text>по</Text>
              <TextInput
                headername="Дата фактического списания"
                deviceatt="dateWriteoffActualUTC"
                name="writeOffActualTimeEnd"
                defaultValue={params.writeoffActualDateEnd || ''}
                min={params.writeoffActualDateStart}
                onChange={(e) => onChangeDate(e, 'end', 'writeoffActualDateStart', 'writeoffActualDateEnd')}
                type="datetime-local"
              />
            </Pane>
          </FormField>

          <FormField label="Дата планируемого списания">
            <Pane className={classes.searchForm}>
              <Text>c</Text>
              <TextInput
                headername="Дата планируемого списания"
                deviceatt="dateWriteoffPlanUTC"
                name="writeoffPlanDateStart"
                defaultValue={params.writeOffPlanTimeStart || ''}
                max={params.writeoffPlanDateEnd}
                onChange={(e) => onChangeDate(e, 'start', 'writeoffPlanDateStart', 'writeoffPlanDateEnd')}
                type="datetime-local"
              />
              <Text>по</Text>
              <TextInput
                headername="Дата планируемого списания"
                deviceatt="dateWriteoffPlanUTC"
                name="writeoffPlanDateEnd"
                defaultValue={params.writeoffPlanDateEnd || ''}
                min={params.writeOffPlanTimeStart}
                onChange={(e) => onChangeDate(e, 'end', 'writeoffPlanDateStart', 'writeoffPlanDateEnd')}
                type="datetime-local"
              />
            </Pane>
          </FormField>
        </Pane>

      </Pane>
    </Details>
  );
});
SearchForm.displayName = 'SearchForm';
